import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, ArrowUpRight, ShieldAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import RequestAlertActions from "@/components/admin/RequestAlertActions";
import { REQUEST_TAGS, requestLink, refreshBadges, timeAgo } from "@/lib/notifications";
import { shortenNos } from "@/lib/caseNo";

// Top-of-dashboard alert for data deletion / role change requests (unread, not dismissed, still waiting).
// Loads when the dashboard opens and again after an action (no polling).
export default function RequestAlertBanner({ tick }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/notifications/requests").then(({ data }) => setItems(data.items)).catch(() => {}); }, [tick]);
  const n = items[0];
  if (!n) return null;
  const role = REQUEST_TAGS[n.type]?.panel === "roles";
  const Icon = role ? ArrowLeftRight : ShieldAlert;
  const accent = role ? "bg-violet-600 hover:bg-violet-700" : "bg-amber-600 hover:bg-amber-700";
  const review = async () => {
    try { await api.post(`/notifications/${n.id}/read`); refreshBadges(); } catch { /* ignore */ }
    navigate(requestLink(n));
  };
  const dismiss = async () => {
    try { await api.post(`/notifications/${n.id}/dismiss-popup`); } catch { /* ignore */ }
    setItems((xs) => xs.slice(1));
  };
  return (
    <div className="sticky top-20 z-10 mb-5" data-testid="request-alert-banner">
      {items.length > 1 && <div className="absolute inset-x-3 -bottom-1.5 h-4 rounded-b-xl border border-slate-200 bg-white/80 shadow-sm" />}
      <div key={n.id} className={`relative flex flex-wrap items-center gap-3 rounded-xl border p-4 shadow-lg backdrop-blur animate-fade-in-up ${role ? "border-violet-200 bg-violet-50/95" : "border-amber-200 bg-amber-50/95"}`}>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${role ? "bg-violet-600" : "bg-amber-500"} text-white`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500" data-testid="request-alert-kind">
            New {REQUEST_TAGS[n.type]?.label.toLowerCase()} · {timeAgo(n.created_at)}
          </p>
          <p className="font-semibold text-slate-900" data-testid="request-alert-title">{n.title}</p>
          <p className="truncate text-sm text-slate-600" data-testid="request-alert-message">{shortenNos(n.message)}</p>
        </div>
        {items.length > 1 && <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-600 shadow-sm" data-testid="request-alert-more">+{items.length - 1} more</span>}
        {n.action ? (
          <>
            <RequestAlertActions n={n} accent={accent} onDone={refreshBadges} />
            <Button variant="ghost" className="rounded-full text-slate-600 hover:bg-white" onClick={review} data-testid="request-alert-review-btn">
              Review <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button className={`rounded-full ${accent}`} onClick={review} data-testid="request-alert-review-btn">
            Review on Users page <ArrowUpRight className="ml-1.5 h-4 w-4" />
          </Button>
        )}
        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-slate-500 hover:bg-white" onClick={dismiss} aria-label="Dismiss alert" data-testid="request-alert-dismiss-btn"><X className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
